import { useCallback, useEffect } from "react";
import { useVideoAnalyticsContext } from "../../contexts/VideoAnalyticsContext";
import { usePerformanceContext } from "../../contexts/PerformanceContext";
import { useAutoplayContext } from "../../contexts/AutoplayContext";
import { useOrcaAdsContext } from "../../contexts/OrcaAdsContext";
import { PerformanceEvents } from "../../../globals";
import { useInitializeNewVideo } from "../useInitializeNewVideo";
import { usePromoContext } from "../../contexts/PromoContext";
import { useStickyVideoContext } from "../../contexts/StickyVideoContext";
import { getIsStickyEnabled } from "../../utils";

/**
 * Custom hook to handle ORCA initialization.
 * It checks if the ORCA components are initialized and calls the provided handler.
 * It also sends performance events related to ORCA readiness.
 * @param {function} handleOrcaInitialized
 */
export const useOrcaInitialized = config => {
  const { isAnalyticsInitialized } = useVideoAnalyticsContext();
  const { sendPerformanceEvent, sendPerformanceMeasure } =
    usePerformanceContext();
  const { updateAutoplayState, autoplayState } = useAutoplayContext();
  const { isAdsInitialized, isIrisReady, shouldShowAds } = useOrcaAdsContext();
  const { setPromoState } = usePromoContext();
  const { setIsStickyEnabled } = useStickyVideoContext();
  const { isCarousel } = config;

  const isOrcaInitialized =
    autoplayState.isInitialized && isAnalyticsInitialized && isAdsInitialized;

  const initializeNewVideo = useInitializeNewVideo({
    setPromoState,
    updateAutoplayState,
    shouldShowAds,
    autoplayState,
    isOrcaInitialized,
    isCarousel
  });

  const handleOrcaInitialized = useCallback(() => {
    initializeNewVideo(autoplayState.isAutoplay);
    setIsStickyEnabled(getIsStickyEnabled(autoplayState, config));
  }, [autoplayState, config, initializeNewVideo, setIsStickyEnabled]);

  useEffect(() => {
    if (!isOrcaInitialized) return;
    handleOrcaInitialized();

    sendPerformanceEvent(PerformanceEvents.ORCA_READY);

    sendPerformanceMeasure(
      PerformanceEvents.ORCA_READY,
      PerformanceEvents.ORCA_RENDER,
      { irisLoaded: isIrisReady.toString() }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrcaInitialized]);

  return { initializeNewVideo };
};
